import { FSection } from '../../mdl/f-page';
import { FPageBlock, FPageDecoration, FPageDecorationKind, FPageSectionPadding } from '../../mdl/f-page-block';
import { uiIsLightBG, uiSetBG } from '../../ui/bg';
import { UITextInput } from '../../ui/component/input';
import { UILabeled } from '../../ui/component/labeled';
import { UISelect } from '../../ui/component/select';
import { UICheckbox } from '../../ui/component/toggle';
import { $el } from '../../ui/elements';
import { uiSetPrefixedClass } from '../../ui/functions';
import { UIValue } from '../../ui/types';
import { UIView } from '../../ui/view';
import { $cdn } from '../../y/cdn';
import { $on, $trigger, YChangeEvent, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $form, YInput } from '../../y/form';
import { YCountryId } from '../../y/types';
import { EDEditor } from '../editor';
import { EDBackgroundInput, EDImageInput } from '../image';
import { EDSection } from '../section';
import { $page_values } from './values';

const section_padding_values: UIValue<FPageSectionPadding>[] = [{ key: 'none', name: 'None' }, { key: 'narrow', name: 'Narrow' }, { key: 'normal', name: 'Normal' }, { key: 'wide', name: 'Wide' }, { key: 'overlap', name: 'Overlap' }];
const section_country_values: UIValue<YCountryId | undefined>[] = [{ key: undefined, name: 'All' }, { key: 'SE', name: 'Sweden' }, { key: 'DE', name: 'Germany' }];
const decoration_kind_values: UIValue<FPageDecorationKind | undefined>[] = [{ key: undefined, name: 'None' }, { key: 'edges', name: 'Edges' }, { key: 'corners', name: 'Corners' }, { key: 'hero', name: 'Hero' }];

class DecorationInput extends UIView implements YInput<FPageDecoration | undefined> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<FPageDecoration | undefined> & YChangeEvent>;

    private _kind: UISelect<FPageDecorationKind | undefined>;
    private _images: EDImageInput[];

    okay = true;
    dirty = false;
    disabled = false;

    constructor(container: HTMLElement) {
        super('div', 'l-form');

        const kind = this._kind = new UISelect(decoration_kind_values, { kind: 'k-subtle' });
        this.ui_add(kind);

        const decos = [$el.DIV(['r-deco', 'r-deco-1']), $el.DIV(['r-deco', 'r-deco-2']), $el.DIV(['r-deco', 'r-deco-3']), $el.DIV(['r-deco', 'r-deco-4'])];
        const images: EDImageInput[] = this._images = [];
        let num_images = 0;

        for(let i = 0; i < decos.length; i++) {
            const image = new EDImageInput({ scope: 'page' });
            const deco = decos[i];
            container.ui_add(deco);
            images.push(image);
            this.ui_add(image);
            image.ui_hide();

            $on(image, 'change', () => {
                const v = image.value;
                if(i < num_images) {
                    deco.style.setProperty('--ui-image', v.image ? `url('${$cdn.media(v.image)}')` : null);
                    deco.style.setProperty('--ui-width', v.width + 'px');
                    deco.style.setProperty('--ui-aspect', v.aspect + '');
                    $trigger(this, 'change');
                }
            });
        }

        $on(kind, 'value', v => {
            uiSetPrefixedClass(container, 'm-decorated', v);
            num_images = v == 'hero' ? 1 : v ? 4 : 0;
            for(let i = 0; i < decos.length; i++) {
                decos[i].ui_show(undefined, i < num_images);
                images[i].ui_show(undefined, i < num_images);
            }
            $trigger(this, 'change');
        });
    }

    get value() {
        const kind = this._kind.value;

        if(kind)
            return { kind: kind, images: this._images.map(i => i.value) }

        return undefined;
    }

    set value(v: FPageDecoration | undefined) {
        if(!v) {
            this._kind.value = undefined;
            return;
        }

        this._kind.value = v.kind;
        this._images[0].value = v.images[0];
        this._images[1].value = v.images[1];
        this._images[2].value = v.images[2];
        this._images[3].value = v.images[3];
    }
}

export class EDPageSection<Editor extends EDEditor<FSection, FPageBlock>> extends EDSection<FSection, FPageBlock> {
    constructor(editor: Editor) {
        const name = new UITextInput({ kind: 'k-subtle', null_if_empty: true });
        const bg = new EDBackgroundInput($page_values.bg_presets, 'page');
        const color_scheme = new UISelect($page_values.color_scheme, { kind: 'k-subtle' });
        const padding_top = new UISelect(section_padding_values, { kind: 'k-subtle' });
        const padding_bottom = new UISelect(section_padding_values, { kind: 'k-subtle' });
        const header = new UICheckbox({ title: 'Header' });
        const bg_sticky = new UICheckbox({ title: 'Sticky Background' });
        const country = new UISelect(section_country_values, { kind: 'k-subtle' });

        const countries = $form.adapt(country, v => {
            return v ? { [v]: true } : {};
        }, v => {
            const keys = Object.keys(v);
            return keys.length > 0 ? keys[0] as YCountryId : undefined;
        })

        const properties_form = $form({ bg, color_scheme, padding_top, padding_bottom, header, bg_sticky })

        const form = $form({ id: $form.noop(), name, properties: properties_form, countries });

        const properties = $el.DIV('l-form')

        super(form, editor, properties);

        const overlap_top = $el.DIV('ui-section-overlap-top');
        const overlap_bottom = $el.DIV('ui-section-overlap-bottom');

        this.ui_insert(overlap_top, this.list);
        this.ui_insert(overlap_bottom, this.list, true);

        const deco = new DecorationInput(this.root);
        properties_form.add('deco', deco);

        properties.ui_add([
            new UILabeled(name, { above: 'Name', below: 'Can be used to scroll to the section' }),
            new UILabeled(bg, { above: 'Background' }),
            new UILabeled(color_scheme, { above: 'Color scheme' }),
            new UILabeled(padding_top, { above: 'Top padding' }),
            new UILabeled(padding_bottom, { above: 'Bottom padding' }),
            new UILabeled(deco, { above: 'Decoration' }),
            header,
            bg_sticky,
            new UILabeled(country, { above: 'Country' }),
        ]);

        $on(bg, 'change', () => {
            const v = bg.value || {};
            uiSetBG(this, v);
            const is_light = uiIsLightBG(v);
            const current = color_scheme.value;
            if(is_light === true) {
                if(current != 'light' && current != 'lgreen')
                    color_scheme.value = 'light';
            } else if(is_light === false) {
                if(current != 'dark' && current != 'green')
                    color_scheme.value = 'dark';
            } else {
                color_scheme.value = 'inherit';
            }
        });

        $on(color_scheme, 'value', v => {
            this.classList.toggle('c-light', v == 'light');
            this.classList.toggle('c-dark', v == 'dark');
            this.classList.toggle('c-green', v == 'green');
            this.classList.toggle('c-lgreen', v == 'lgreen');
        })

        $on(header, 'value', v => {
            this.classList.toggle('m-700', v);
        });

        $on(padding_top, 'value', v => {
            overlap_top.ui_show(undefined, v == 'overlap');
            uiSetPrefixedClass(this, 'pad-t', v);
            this.classList.toggle('m-overflow', deco.value?.kind == 'hero' || padding_top.value == 'overlap' || padding_bottom.value == 'overlap');
        });

        $on(padding_bottom, 'value', v => {
            overlap_bottom.ui_show(undefined, v == 'overlap');
            uiSetPrefixedClass(this, 'pad-b', v);
            this.classList.toggle('m-overflow', deco.value?.kind == 'hero' || padding_top.value == 'overlap' || padding_bottom.value == 'overlap');
        });

        $on(deco, 'change', () => {
            this.classList.toggle('m-overflow', deco.value?.kind == 'hero' || padding_top.value == 'overlap' || padding_bottom.value == 'overlap');
        });

        $on(bg_sticky, 'value', v => {
            this.classList.toggle('m-sticky-bg', !!v);
        });
    }
}
